<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = withDefaults(defineProps<{
  color?: string | string[];
  colored?: boolean;
  mobileMode?: boolean;
}>(), {
  color: "G",
  colored: false,
  mobileMode: false,
});

const host = process.env.VUE_APP_HOST;

const colors = [
  {
    key: "j",
    label: "J",
    img: "/img/diamonds/details/color_j.webp",
  },
  {
    key: "i",
    label: "I",
    img: "/img/diamonds/details/color_i.webp",
  },
  {
    key: "h",
    label: "H",
    img: "/img/diamonds/details/color_h.webp",
  },
  {
    key: "g",
    label: "G",
    img: "/img/diamonds/details/color_g.webp",
  },
  {
    key: "f",
    label: "F",
    img: "/img/diamonds/details/color_f.webp",
  },
  {
    key: "e",
    label: "E",
    img: "/img/diamonds/details/color_e.webp",
  },
  {
    key: "d",
    label: "D",
    img: "/img/diamonds/details/color_d.webp",
  },
];

const colorValue = computed(() => Array.isArray(props.color) ? props.color[0] : props.color);

const getInitialIndex = () => {
  if (!colorValue.value) return 6;
  const index = colors.findIndex((c) => c.key === colorValue.value!.toLowerCase());
  return index !== -1 ? index : 6;
};

const selectedIndex = ref(getInitialIndex());

const isMobile = ref(window.innerWidth < 769);
const updateMobile = () => {
  isMobile.value = window.innerWidth < 769;
};

const isMobileMode = computed(() => props.mobileMode || isMobile.value);

watch(() => props.color, () => {
  selectedIndex.value = getInitialIndex();
});

const sliderRef = ref<HTMLElement | null>(null);
const isDragging = ref(false);

const updateIndexFromEvent = (e: MouseEvent | TouchEvent) => {
  if (!sliderRef.value) return;
  const rect = sliderRef.value.getBoundingClientRect();
  const clientX =
    "touches" in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
  const offsetX = clientX - rect.left;
  let percentage = offsetX / rect.width;
  percentage = Math.max(0, Math.min(0.99, percentage));
  selectedIndex.value = Math.floor(percentage * 7);
};

const startDragging = (e: MouseEvent | TouchEvent) => {
  if (props.colored) return;
  isDragging.value = true;
  updateIndexFromEvent(e);
};

const handleMove = (e: MouseEvent | TouchEvent) => {
  if (isDragging.value) {
    updateIndexFromEvent(e);
  }
};

const stopDragging = () => {
  isDragging.value = false;
};

onMounted(() => {
  window.addEventListener("mousemove", handleMove);
  window.addEventListener("mouseup", stopDragging);
  window.addEventListener("touchmove", handleMove, { passive: false });
  window.addEventListener("touchend", stopDragging);
  window.addEventListener("resize", updateMobile);
});

onUnmounted(() => {
  window.removeEventListener("mousemove", handleMove);
  window.removeEventListener("mouseup", stopDragging);
  window.removeEventListener("touchmove", handleMove);
  window.removeEventListener("touchend", stopDragging);
  window.removeEventListener("resize", updateMobile);
});
</script>

<template>
  <div :class="['flex flex-col items-center ', isMobileMode ? '' : 'md:gap-12 md:flex-row']">
    <!-- Left column: info text -->
    <div :class="['w-full text-left', isMobileMode ? '' : 'md:w-1/3']">
      <slot name="title" :selectedLabel="colored ? colorValue : colors[selectedIndex].label">
        <h3 class="text-2xl font-bold mb-6">
          {{ t("components.diamonds.details.color.title") }}:
          {{ colored ? colorValue : colors[selectedIndex].label }}
        </h3>
      </slot>
      <p class="text-black text-left font-light text-xs leading-relaxed whitespace-pre-line">
        <template v-if="colored && colorValue">
          {{
            t(
              "components.diamonds.details.color.colored." +
                colorValue.toLowerCase() +
                ".text"
            )
          }}
        </template>
        <template v-else>
          {{ t("components.diamonds.details.color.content_text") }}
        </template>
      </p>
    </div>

    <!-- Right column: Slider -->
    <div :class="['w-full', isMobileMode ? '' : 'md:w-2/3']">
      <div v-if="colored && colorValue" class="flex justify-center">
        <img
          :src="
            host +
            '/img/diamonds/filter/color/' +
            colorValue.toUpperCase() +
            '@2x.jpg'
          "
          class="h-full max-h-64 mx-auto rounded-md"
        />
      </div>
      <div v-else class="flex flex-col">
        <!-- Images -->
        <div :class="['flex justify-center items-end mb-6', isMobileMode ? '' : 'md:justify-between']">
          <div
            v-for="(c, index) in colors"
            :key="c.key"
            class="justify-center px-2 flex"
            :class="[
              selectedIndex === index ? 'flex' : (isMobileMode ? 'hidden' : 'hidden md:flex'),
              isMobileMode ? '' : 'md:flex-1',
            ]"
          >
            <img
              :src="host + c.img"
              :alt="c.label"
              class=" transition-opacity duration-300 cursor-pointer"
              :class="[
                isMobileMode ? 'h-52 w-80 object-cover max-w-[420px]' : 'max-w-[420px] md:w-full',
                { 'md:opacity-90': !isMobileMode && selectedIndex !== index }
              ]"
              @click="selectedIndex = index"
            />
          </div>
        </div>

        <!-- Slider -->
        <div
          class="relative pb-32 cursor-pointer"
          ref="sliderRef"
          @mousedown="startDragging"
          @touchstart.prevent="startDragging"
        >
          <div class="flex gap-1.5 h-1">
            <div
              v-for="(c, index) in colors"
              :key="'bar-' + c.key"
              class="flex-1 cursor-pointer relative group"
              @click="selectedIndex = index"
            >
              <!-- Active/Inactive Bar -->
              <div
                class="h-full w-full rounded-full transition-colors duration-300"
                :class="
                  selectedIndex === index ? 'bg-brand-main' : 'bg-brand-main/30'
                "
              ></div>

              <!-- Label -->
              <span
                class="absolute top-2 left-1/2 -translate-x-1/2 text-sm transition-colors duration-300 whitespace-nowrap"
                :class="
                  selectedIndex === index
                    ? 'text-brand-main font-medium'
                    : 'text-gray-400'
                "
              >
                {{ c.label }}
              </span>
            </div>
          </div>

          <!-- Description Bubble -->
          <div class="absolute left-0 right-0 mt-10 transition-all duration-300">
            <div class="bg-brand-main rounded-xl p-2 text-white text-center relative">
              <!-- Triangle pointing up -->
              <div
                class="absolute -top-2 w-0 h-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-b-[10px] border-b-brand-main transition-all duration-300"
                :style="{
                  left: `calc(${
                    (selectedIndex * 100) / 7 + 100 / 14
                  }% - 10px)`,
                }"
              ></div>
              <p class="text-xs leading-relaxed">
                {{ t("components.diamonds.details.color.descriptions." + colors[selectedIndex].key) }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
